import {
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Tooltip,
  Typography,
  type TypographyProps
} from '@mui/material';
import type { User } from 'app/collab/types';
import { useMemo } from 'react';
import styled from 'styled-components';
import { UserAvatar } from '../user-avatar/index.js';
import { UserAvatarGroup } from '../user-avatar-group/index.js';

export type ContentItemCollabStateProps = {
  readonly viewingUsers: User[];
  readonly currentUserId?: string;
  readonly editingUser?: User;
};

export type ContentItemCollabOwnProps = {
  readonly contentId: string;
  readonly includeText?: boolean;
  readonly hideTooltip?: boolean;
  readonly viewAvatarCount?: number;
};

export type ContentItemCollabProps = ContentItemCollabOwnProps & ContentItemCollabStateProps;

const getFullName = (user: User) => user.fullName ?? user.email;
const shortenName = (name: string) => name.split(' ')[0];

export function getCollabMessage(viewingUsers: User[], currentUserId?: string, editingUser?: User) {
  let finalMessage = '';
  if (editingUser && editingUser.id !== currentUserId) {
    const editingUserFullName = getFullName(editingUser);
    if (viewingUsers.length) {
      finalMessage = `${shortenName(editingUserFullName)} is editing, `;
    } else {
      finalMessage = `${editingUserFullName} is editing`;
    }
  }

  if (viewingUsers.length) {
    finalMessage += `${
      viewingUsers.length === 1 ? `${shortenName(getFullName(viewingUsers[0]))} is` : `${viewingUsers.length} are`
    } viewing`;
  }

  if (finalMessage === '') {
    return 'Content Locked';
  }

  return `${finalMessage}.`;
}

const Container = styled.div`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(1)};
`;

export function UserList({ viewing, editing }: { readonly viewing: User[]; readonly editing?: User }) {
  const secondaryTextProps: TypographyProps = {
    color: 'inherit',
    style: { opacity: 0.6 }
  };
  const avatarSize = 2.8;
  return (
    <ul>
      {editing && (
        <>
          <ListItem key={editing.id}>
            <ListItemAvatar>
              <UserAvatar user={editing} size={avatarSize} />
            </ListItemAvatar>
            <ListItemText
              primary={editing.fullName ?? editing.email}
              secondary="Editing"
              secondaryTypographyProps={secondaryTextProps}
            />
          </ListItem>
          <Divider light />
        </>
      )}
      {viewing.map((user) => {
        const name = user.fullName ?? user.email;
        return (
          <ListItem key={user.id}>
            <ListItemAvatar>
              <UserAvatar user={user} size={avatarSize} />
            </ListItemAvatar>
            <ListItemText primary={name} secondary="Viewing" secondaryTypographyProps={secondaryTextProps} />
          </ListItem>
        );
      })}
    </ul>
  );
}

export function ContentItemCollab(props: ContentItemCollabProps) {
  const { currentUserId, editingUser, viewingUsers, includeText, viewAvatarCount } = props;

  const users = useMemo(() => {
    return viewingUsers.concat(editingUser ?? []);
  }, [editingUser, viewingUsers]);

  const collabMessage = useMemo(
    () => getCollabMessage(viewingUsers, currentUserId, editingUser),
    [currentUserId, viewingUsers, editingUser]
  );

  const userList = useMemo(
    () => <UserList viewing={viewingUsers} editing={editingUser} />,
    [viewingUsers, editingUser]
  );

  return (
    <Tooltip title={userList}>
      <Container>
        <UserAvatarGroup users={users} max={viewAvatarCount} />
        {includeText && <Typography>{collabMessage}</Typography>}
      </Container>
    </Tooltip>
  );
}
